/**
 * Lightweight self-hosted crash/error reporter — no third-party SDK/account
 * needed. Captures uncaught JS errors + unhandled promise rejections in the
 * WebView (the vast majority of "crashes" in a Capacitor app) and ships them
 * to our own API for triage.
 */
import { Capacitor } from "@capacitor/core";
import { API_BASE } from "./apiBase";

type GameSnapshot = {
  phase?: string;
  distance?: number;
  difficulty?: string;
};

let getSnapshot: () => GameSnapshot = () => ({});
let sent = 0;
const MAX_PER_SESSION = 20;

/** Wire in a getter for lightweight game context (phase/distance/difficulty). */
export function setCrashContext(fn: () => GameSnapshot) {
  getSnapshot = fn;
}

function post(payload: Record<string, unknown>) {
  if (sent >= MAX_PER_SESSION) return;
  sent += 1;
  try {
    const body = JSON.stringify(payload);
    const url = `${API_BASE}/api/crash`;
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* never let the reporter itself crash the app */
  }
}

function baseFields() {
  return {
    ts: new Date().toISOString(),
    platform: Capacitor.getPlatform(),
    ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
    appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "1.0.0",
    ...getSnapshot(),
  };
}

export function reportError(error: unknown, tag: string) {
  const err = error instanceof Error ? error : new Error(String(error));
  post({
    ...baseFields(),
    tag,
    message: err.message,
    stack: err.stack?.slice(0, 4000),
  });
}

let installed = false;

/** Call once at app startup. */
export function initCrashReporter() {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (ev) => {
    post({
      ...baseFields(),
      tag: "window.onerror",
      message: ev.message,
      stack: ev.error?.stack?.slice(0, 4000),
      source: `${ev.filename}:${ev.lineno}:${ev.colno}`,
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    const err = reason instanceof Error ? reason : new Error(String(reason));
    post({
      ...baseFields(),
      tag: "unhandledrejection",
      message: err.message,
      stack: err.stack?.slice(0, 4000),
    });
  });
}
